import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, Save, Trophy, AlertTriangle, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMPTS = [
  {
    key: 'biggest_win',
    label: "Biggest win this week",
    icon: Trophy,
    color: 'emerald',
    placeholder: "What went really well? A great training session, a kid who finally clicked, a tough match handled well...",
  },
  {
    key: 'biggest_drain',
    label: "Biggest drain this week",
    icon: AlertTriangle,
    color: 'amber',
    placeholder: "What took the most energy out of you? Parent issue, admin overload, a tough conversation...",
  },
  {
    key: 'one_change',
    label: "One thing to change next week",
    icon: Lightbulb,
    color: 'blue',
    placeholder: "If you could change one thing about next week, what would it be?",
  },
];

function formatWeek(weekStart) {
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts = { day: 'numeric', month: 'short' };
  return `${start.toLocaleDateString('en-GB', opts)} – ${end.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })}`;
}

export default function WelfareReflections() {
  const [values, setValues] = useState({ biggest_win: '', biggest_drain: '', one_change: '' });
  const [currentExists, setCurrentExists] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [curRes, histRes] = await Promise.all([
        axios.get(`${API}/welfare/reflection/current`),
        axios.get(`${API}/welfare/reflection/history`),
      ]);
      if (curRes.data) {
        setCurrentExists(true);
        setValues({
          biggest_win: curRes.data.biggest_win || '',
          biggest_drain: curRes.data.biggest_drain || '',
          one_change: curRes.data.one_change || '',
        });
      }
      setHistory(histRes.data || []);
    } catch (error) {
      console.error('Failed to load reflections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!values.biggest_win.trim() && !values.biggest_drain.trim() && !values.one_change.trim()) {
      toast.error('Add at least one reflection before saving');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/welfare/reflection`, {
        biggest_win: values.biggest_win.trim() || null,
        biggest_drain: values.biggest_drain.trim() || null,
        one_change: values.one_change.trim() || null,
      });
      toast.success(currentExists ? 'Reflection updated' : 'Reflection saved');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  // Filter history to exclude the current week from past list
  const pastHistory = history.filter(h => {
    if (!currentExists) return true;
    return history[0] ? h.week_start !== history[0].week_start : true;
  });

  return (
    <div className="space-y-6" data-testid="welfare-reflections-container">
      <Card data-testid="welfare-reflection-form">
        <CardHeader>
          <CardTitle className="text-xl">This week's reflection</CardTitle>
          <p className="text-sm text-gray-500">Optional but powerful. Even a one-liner per prompt builds a useful pattern over time.</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {PROMPTS.map(p => {
            const Icon = p.icon;
            return (
              <div key={p.key} data-testid={`welfare-prompt-${p.key}`}>
                <label className={`flex items-center gap-2 text-${p.color}-700 font-medium mb-2`}>
                  <Icon className={`w-4 h-4 text-${p.color}-500`} strokeWidth={1.5} />
                  {p.label}
                </label>
                <Textarea
                  value={values[p.key]}
                  onChange={(e) => setValues(prev => ({ ...prev, [p.key]: e.target.value }))}
                  placeholder={p.placeholder}
                  rows={3}
                  maxLength={1000}
                  data-testid={`welfare-textarea-${p.key}`}
                />
              </div>
            );
          })}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="welfare-reflection-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />{currentExists ? 'Update Reflection' : 'Save Reflection'}</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {pastHistory.length > 0 && (
        <Card data-testid="welfare-reflection-history">
          <CardHeader>
            <CardTitle className="text-base">Past reflections</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pastHistory.map(r => {
              const isOpen = expanded.has(r.id);
              return (
                <div
                  key={r.id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                  data-testid={`welfare-reflection-${r.id}`}
                >
                  <button
                    onClick={() => toggleExpand(r.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <span className="font-medium text-gray-900">{formatWeek(r.week_start)}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 space-y-3 text-sm">
                      {r.biggest_win && (
                        <div>
                          <p className="text-emerald-700 font-medium flex items-center gap-1.5 mb-1"><Trophy className="w-3.5 h-3.5" />Win</p>
                          <p className="text-gray-700 whitespace-pre-wrap">{r.biggest_win}</p>
                        </div>
                      )}
                      {r.biggest_drain && (
                        <div>
                          <p className="text-amber-700 font-medium flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Drain</p>
                          <p className="text-gray-700 whitespace-pre-wrap">{r.biggest_drain}</p>
                        </div>
                      )}
                      {r.one_change && (
                        <div>
                          <p className="text-blue-700 font-medium flex items-center gap-1.5 mb-1"><Lightbulb className="w-3.5 h-3.5" />Change</p>
                          <p className="text-gray-700 whitespace-pre-wrap">{r.one_change}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
